import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import SortedSelect from 'js/components/elements/rselect/SortedSelect';
import { FieldBuilderRedesigned } from 'new/elements/FieldBuilderRedesigned/FieldBuilder';
import UIUtils from 'js/lib/UIUtils';
import classNames from 'classnames';
import Tooltip, { DefaultTooltipPopperProps } from 'js/components/elements/mui/Tooltip';
import { MarkdownContent } from 'new/elements/MarkdownContent';
import { ReactComponent as InfoIcon } from 'new/components/icons/fi-rr-info.svg';
import styles from './SelectWithDependencies.module.scss';
import { ReactComponent as DeleteIcon } from 'new/components/icons/fi-rr-trash.svg';
import { ReactComponent as UnfoldMoreIcon } from 'new/components/icons/UnfoldMoreFilled.svg';
import { ReactComponent as UnfoldLessIcon } from 'new/components/icons/UnfoldLessFilled.svg';
import { components } from 'react-select';

interface SelectOption {
  label: string;
  value: string | boolean;
  description?: string;
}

export interface SelectWithDependenciesProps {
  field: {
    name: string;
    display_name: string;
    description?: string;
    values: SelectOption[];
    isMulti?: boolean;
    default?: string;
    type?: string; // Add type to distinguish between select and boolean fields
    disabledOn?: Array<{ name: string; value: any }>; // Options to hide from dropdown
  };
  allFields: any[];
  currentValues: Record<string, any>;
  onChange: (fieldName: string, value: any) => void;
  currentFieldValue: any;
  id?: string;
  disabled?: boolean;
  className?: string;
  isSingleSelect?: boolean;
}

const CustomExpandIcon = ({ expanded }: { expanded: boolean }) => (
  <Box className={styles.expandIconWrapper}>
    {expanded ? (
      <UnfoldLessIcon className={styles.expandIcon} />
    ) : (
      <UnfoldMoreIcon className={styles.expandIcon} />
    )}
  </Box>
);

// Custom Control component to properly apply data-testid to react-select
const CustomControl = ({ children, ...props }: any) => {
  const { selectProps, innerProps } = props;
  const testId = selectProps?.['data-testid'];

  return (
    <components.Control
      {...props}
      innerProps={{
        ...innerProps,
        ...(testId && { 'data-testid': testId }),
      }}
    >
      {children}
    </components.Control>
  );
};

export const SelectWithDependencies: React.FC<SelectWithDependenciesProps> = ({
  field,
  allFields,
  currentValues,
  onChange,
  currentFieldValue,
  id,
  disabled = false,
  className,
  isSingleSelect = true,
}) => {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [lastSelected, setLastSelected] = useState<string | null>(null);

  const defaultOption = useMemo(() => {
    return field.values.find((option) => option.value === field.default) || null;
  }, [field.values, field.default]);

  const hasOnlyDefaultOption = useMemo(() => {
    if (!defaultOption) return false;
    if (Array.isArray(currentFieldValue)) {
      return currentFieldValue.length === 1 && currentFieldValue[0] === defaultOption.value;
    }
    return currentFieldValue === defaultOption.value;
  }, [currentFieldValue, defaultOption]);

  // Check if this is a boolean field (checkbox)
  const isBooleanField = field.type === 'boolean';

  // For boolean fields, get the current value
  const booleanFieldValue = isBooleanField ? currentValues[field.name] : false;

  // Get dependent fields for a specific field name and value
  const getDependentFields = useCallback(
    (parentName: string, parentValue: any) => {
      return allFields.filter((f) => {
        if (!f.dependsOn) return false;

        const dependsOn = f.dependsOn;

        // Simple case: direct dependency
        if (dependsOn.name === parentName && dependsOn.value === parentValue) {
          return true;
        }

        // anyOf support
        if (dependsOn.anyOf) {
          return dependsOn.anyOf.some(
            (cond: any) => cond.name === parentName && cond.value === parentValue,
          );
        }

        // allOf support
        if (dependsOn.allOf) {
          return dependsOn.allOf.some(
            (cond: any) => cond.name === parentName && cond.value === parentValue,
          );
        }

        return false;
      });
    },
    [allFields],
  );

  // Get boolean fields that depend on the current select field
  const getBooleanDependentFields = useCallback(
    (parentName: string, parentValue: any) => {
      return allFields.filter((f) => {
        if (!f.dependsOn || f.type !== 'boolean') {
          return false;
        }

        const dependsOn = f.dependsOn;

        // Simple case: direct dependency
        if (dependsOn.name === parentName && dependsOn.value === parentValue) {
          return true;
        }

        // anyOf support
        if (dependsOn.anyOf) {
          return dependsOn.anyOf.some(
            (cond: any) => cond.name === parentName && cond.value === parentValue,
          );
        }

        // allOf support
        if (dependsOn.allOf) {
          return dependsOn.allOf.some(
            (cond: any) => cond.name === parentName && cond.value === parentValue,
          );
        }

        return false;
      });
    },
    [allFields],
  );

  // Get non-boolean dependent fields (to exclude checkboxes from select field dependents)
  const getNonBooleanDependentFields = useCallback(
    (parentName: string, parentValue: any) => {
      const booleanDependentFields = getBooleanDependentFields(parentName, parentValue);

      return allFields.filter((f) => {
        if (!f.dependsOn || f.type === 'boolean') {
          return false;
        }

        const dependsOn = f.dependsOn;

        // Check if this field depends on any boolean field (including complex dependencies)
        const dependsOnBooleanField = booleanDependentFields.some((booleanField) => {
          // Simple case: direct dependency on boolean field
          if (dependsOn.name === booleanField.name) {
            return true;
          }

          // Check allOf dependencies
          if (dependsOn.allOf) {
            return dependsOn.allOf.some((cond: any) => cond.name === booleanField.name);
          }

          // Check anyOf dependencies
          if (dependsOn.anyOf) {
            return dependsOn.anyOf.some((cond: any) => cond.name === booleanField.name);
          }

          return false;
        });

        if (dependsOnBooleanField) {
          return false;
        }

        // Simple case: direct dependency
        if (dependsOn.name === parentName && dependsOn.value === parentValue) {
          return true;
        }

        // anyOf support
        if (dependsOn.anyOf) {
          return dependsOn.anyOf.some(
            (cond: any) => cond.name === parentName && cond.value === parentValue,
          );
        }

        // allOf support
        if (dependsOn.allOf) {
          return dependsOn.allOf.some(
            (cond: any) => cond.name === parentName && cond.value === parentValue,
          );
        }

        return false;
      });
    },
    [allFields, getBooleanDependentFields],
  );

  // For select-type fields, get selected options
  const selectedOptions = useMemo(() => {
    if (isBooleanField) return [];

    let options = [];
    if (Array.isArray(currentFieldValue)) {
      options = field.values.filter((opt) => currentFieldValue.includes(opt.value));
    } else if (currentFieldValue !== undefined && currentFieldValue !== null) {
      options = field.values.filter((opt) => currentFieldValue === opt.value);
    }
    return options;
  }, [field.values, currentFieldValue, isBooleanField]);

  // Ensure first selected option is expanded by default when it has dependents
  useEffect(() => {
    if (isBooleanField) return;
    if (!selectedOptions || selectedOptions.length === 0) return;

    // If any selected option is already expanded, respect user's choice
    const anyExpanded = selectedOptions.some((opt) => expandedItems.has(opt.value as string));
    if (anyExpanded) return;

    const firstSelected = selectedOptions[0];
    if (!firstSelected) return;

    const nonBooleanDeps = getNonBooleanDependentFields(field.name, firstSelected.value);
    const booleanDeps = getBooleanDependentFields(field.name, firstSelected.value);
    const hasDependents = nonBooleanDeps.length > 0 || booleanDeps.length > 0;

    if (hasDependents && !expandedItems.has(firstSelected.value as string)) {
      setExpandedItems((prev) => {
        if (prev.has(firstSelected.value as string)) return prev;
        const newSet = new Set(prev);
        newSet.add(firstSelected.value as string);
        return newSet;
      });
    }
  }, [
    selectedOptions,
    isBooleanField,
    getNonBooleanDependentFields,
    getBooleanDependentFields,
    field.name,
    expandedItems,
  ]);

  const handleDependentFieldChange = useCallback(
    (fieldName: string, value: any, isButtonField?: boolean) => {
      onChange(fieldName, value);
    },
    [onChange],
  );

  // Add this helper function above renderSelectField or inside it:
  function renderFieldAndDependents(
    field,
    allFields,
    currentValues,
    onChange,
    disabled,
    getDependentFields,
    id,
    parentValue,
  ) {
    // Only render if shouldRenderField is true
    const shouldRender = UIUtils.shouldRenderField(field, currentValues);

    if (!shouldRender) return null;

    const isBoolean = field.isBoolean;
    let fieldNode;
    if (isBoolean) {
      const booleanFieldValue = currentValues[field.name] ?? false;
      const booleanFieldDependents = getDependentFields(field.name, true);
      fieldNode = (
        <div key={field.name} className={styles.booleanFieldContainer}>
          <FieldBuilderRedesigned
            field={field}
            currentFieldValue={booleanFieldValue}
            onChange={onChange}
            id={`${id}_${field.name}`}
            disabled={disabled}
            allFields={allFields}
            currentValues={currentValues}
            fieldLabelClassName="field-label-name"
          />

          {booleanFieldValue && booleanFieldDependents.length > 0 && (
            <div className={styles.dependentFields}>
              {booleanFieldDependents.map((depField) =>
                renderFieldAndDependents(
                  depField,
                  allFields,
                  currentValues,
                  onChange,
                  disabled,
                  getDependentFields,
                  id,
                  true,
                ),
              )}
            </div>
          )}
        </div>
      );
    } else {
      // For non-boolean fields
      const currentFieldValue = currentValues[field.name] ?? field.default ?? null;
      fieldNode = (
        <div key={field.name} className={styles.dependentField}>
          <FieldBuilderRedesigned
            field={field}
            currentFieldValue={currentFieldValue}
            onChange={onChange}
            id={`${id}_${field.name}`}
            disabled={disabled}
            allFields={allFields}
            currentValues={currentValues}
            fieldLabelClassName="field-label-name"
          />
        </div>
      );
    }

    // For non-boolean fields, find and render their dependents
    if (!isBoolean) {
      const dependents = allFields.filter((f) => {
        if (!f.dependsOn) return false;
        if (f.dependsOn.name === field.name) return true;
        if (f.dependsOn.allOf) return f.dependsOn.allOf.some((cond) => cond.name === field.name);
        if (f.dependsOn.anyOf) return f.dependsOn.anyOf.some((cond) => cond.name === field.name);
        return false;
      });

      return (
        <React.Fragment key={field.name}>
          {fieldNode}
          {dependents.map((dep) =>
            renderFieldAndDependents(
              dep,
              allFields,
              currentValues,
              onChange,
              disabled,
              getDependentFields,
              id,
              currentValues[field.name],
            ),
          )}
        </React.Fragment>
      );
    }

    // For boolean fields, just return the field node (dependents are already rendered inside)
    return fieldNode;
  }

  // Render boolean field (checkbox) with its dependents
  const renderBooleanField = () => {
    const dependentFields = getDependentFields(field.name, true);

    return (
      <div className={styles.booleanFieldContainer}>
        <FieldBuilderRedesigned
          field={field}
          currentFieldValue={booleanFieldValue}
          onChange={onChange}
          id={id}
          disabled={disabled}
          allFields={allFields}
          currentValues={currentValues}
          fieldLabelClassName="field-label-name"
        />

        {booleanFieldValue &&
          dependentFields.length > 0 &&
          dependentFields.map((depField) =>
            renderFieldAndDependents(
              depField,
              allFields,
              currentValues,
              onChange,
              disabled,
              getNonBooleanDependentFields,
              id,
              booleanFieldValue,
            ),
          )}
      </div>
    );
  };

  // Render select field (original logic)
  const renderSelectField = () => {
    return (
      <div className={styles.selectContainerWrapper}>
        <div className={styles.selectContainer}>
          <SortedSelect
            variant="default"
            data-testid="file-parser-dropdown"
            components={{ Control: CustomControl }}
            options={field.values.filter((option) => {
              // Hide options that are listed in disabledOn (e.g., "Raw File Data" controlled by checkbox)
              const isDisabledOption = field.disabledOn?.some(
                (disabled: any) => disabled.value === option.value,
              );

              return (
                !isDisabledOption &&
                !selectedOptions.some((sel) => sel.value === option.value) &&
                !(defaultOption && option.value === defaultOption.value && hasOnlyDefaultOption)
              );
            })}
            value={null}
            onChange={(option) => {
              if (!option) return;

              let newSelected;
              if (isSingleSelect) {
                newSelected = option.value;
              } else {
                const currentSelections = selectedOptions
                  .filter((opt) => !defaultOption || opt.value !== defaultOption.value)
                  .map((opt) => opt.value);

                newSelected = [...currentSelections, option.value];
              }

              onChange(field.name, newSelected);
              setLastSelected(option.value);
            }}
            isDisabled={disabled}
            placeholder={isSingleSelect ? 'Choose a different parser' : 'Add parser...'}
            className={styles.select}
            isClearable={false}
            isSearchable
          />
        </div>

        <div className={styles.selectedOptions}>
          {selectedOptions.map((option) => {
            // Use non-boolean dependent fields for select options
            const dependentFields = getNonBooleanDependentFields(field.name, option.value);

            const isExpanded = expandedItems.has(option.value);

            // Get boolean fields that depend on this option
            const optionBooleanFields = getBooleanDependentFields(field.name, option.value);

            // For options listed in disabledOn (e.g., "Raw File Data"), render dependent fields
            // without accordion wrapper since they're controlled by external UI (checkbox)
            const isDisabledOption = field.disabledOn?.some(
              (disabled: any) => disabled.value === option.value,
            );

            if (isDisabledOption) {
              return (
                <div key={option.value} className={styles.dependentFieldsOnly}>
                  {optionBooleanFields.map((boolField) =>
                    renderFieldAndDependents(
                      boolField,
                      allFields,
                      currentValues,
                      onChange,
                      disabled,
                      getNonBooleanDependentFields,
                      id,
                      option.value,
                    ),
                  )}
                  {dependentFields.map((depField) =>
                    renderFieldAndDependents(
                      depField,
                      allFields,
                      currentValues,
                      onChange,
                      disabled,
                      getNonBooleanDependentFields,
                      id,
                      option.value,
                    ),
                  )}
                </div>
              );
            }

            return (
              <Accordion
                key={option.value}
                expanded={
                  dependentFields.length > 0 || optionBooleanFields.length > 0 ? isExpanded : true
                }
                onChange={() => {
                  if (dependentFields.length === 0 && optionBooleanFields.length === 0) return;
                  setExpandedItems((prev) => {
                    const newSet = new Set(prev);
                    if (newSet.has(option.value)) newSet.delete(option.value);
                    else newSet.add(option.value);
                    return newSet;
                  });
                }}
                disabled={disabled}
                square
                elevation={0}
                className={styles.accordion}
              >
                <AccordionSummary
                  expandIcon={
                    dependentFields.length > 0 || optionBooleanFields.length > 0 ? (
                      <CustomExpandIcon expanded={isExpanded} />
                    ) : null
                  }
                  aria-controls={`panel-${option.value}-content`}
                  id={`panel-${option.value}-header`}
                  className={styles.accordionSummary}
                >
                  <Box>
                    <Typography variant="body2" className={styles.optionLabel}>
                      {option.label}
                    </Typography>
                    {option.description && (
                      <div className={styles.optionDescriptionWrapper}>
                        <Typography variant="caption" className={styles.optionDescription}>
                          {option.description}
                        </Typography>
                      </div>
                    )}
                  </Box>
                  <div className={styles.accordionActions}>
                    {(!defaultOption || option.value !== defaultOption.value) && (
                      <DeleteIcon
                        className={styles.deleteIcon}
                        onClick={(e) => {
                          e.stopPropagation();

                          let newSelected;
                          if (isSingleSelect) {
                            newSelected = defaultOption ? defaultOption.value : null;
                          } else {
                            newSelected = selectedOptions
                              .filter((opt) => opt.value !== option.value)
                              .map((opt) => opt.value);

                            if (newSelected.length === 0 && defaultOption) {
                              newSelected = [defaultOption.value];
                            }
                          }

                          onChange(field.name, newSelected);
                          setExpandedItems((prev) => {
                            const newSet = new Set(prev);
                            newSet.delete(option.value);
                            return newSet;
                          });
                          if (lastSelected === option.value) setLastSelected(null);
                        }}
                      />
                    )}
                  </div>
                </AccordionSummary>
                {(dependentFields.length > 0 || optionBooleanFields.length > 0) && (
                  <AccordionDetails className={styles.accordionDetails}>
                    {/* Render fields in config order */}
                    {(() => {
                      // Get all dependent fields (both boolean and non-boolean)
                      const allDependentFields = [
                        ...dependentFields.map((field) => ({
                          ...field,
                          isBoolean: false,
                        })),
                        ...optionBooleanFields.map((field) => ({
                          ...field,
                          isBoolean: true,
                        })),
                      ];

                      // Sort them based on their position in the allFields array to maintain config order
                      const sortedFields = allDependentFields.sort((a, b) => {
                        const aIndex = allFields.findIndex((f) => f.name === a.name);
                        const bIndex = allFields.findIndex((f) => f.name === b.name);
                        return aIndex - bIndex;
                      });

                      const satisfiedFields = sortedFields.filter((field) =>
                        UIUtils.shouldRenderField(field, currentValues),
                      );

                      // Log which fields are NOT satisfied and why
                      sortedFields.forEach((field) => {
                        const shouldRender = UIUtils.shouldRenderField(field, currentValues);
                      });

                      // Log dependencies for each field
                      satisfiedFields.forEach((field) => {
                        if (field.dependsOn) {
                          let depInfo = '';
                          if (field.dependsOn.name) {
                            depInfo = `depends on: ${field.dependsOn.name}`;
                          } else if (field.dependsOn.allOf) {
                            depInfo = `depends on allOf: ${field.dependsOn.allOf.map((c) => c.name).join(', ')}`;
                          } else if (field.dependsOn.anyOf) {
                            depInfo = `depends on anyOf: ${field.dependsOn.anyOf.map((c) => c.name).join(', ')}`;
                          }
                        }
                      });

                      const topLevelFields = satisfiedFields.filter(
                        (field) => !isChildOfAnyField(field, satisfiedFields),
                      );

                      // Log which fields are being filtered out and why
                      satisfiedFields.forEach((field) => {
                        const isChild = isChildOfAnyField(field, satisfiedFields);
                      });

                      // Simple approach: render all satisfied fields, but skip children
                      return (
                        <>
                          {topLevelFields.map((field) =>
                            renderFieldAndDependents(
                              field,
                              allFields,
                              currentValues,
                              onChange,
                              disabled,
                              getNonBooleanDependentFields,
                              id,
                              option.value,
                            ),
                          )}
                        </>
                      );
                    })()}
                  </AccordionDetails>
                )}
              </Accordion>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div
      className={classNames(
        styles.container,
        className,
        'select-with-dependencies',
        'advanced-configuration',
        {
          [styles.singleSelect]: isSingleSelect,
        },
      )}
    >
      {!field.description ? (
        <label className="field-label-name">{field.display_name}</label>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
          <label className="field-label-name">{field.display_name}</label>
          <Tooltip
            interactive="true"
            placement="right"
            rootStyle={{}}
            arrow={false}
            tooltipTheme="fieldInfo"
            PopperProps={DefaultTooltipPopperProps}
            title={<MarkdownContent>{field.description}</MarkdownContent>}
            children={
              <div style={{ marginTop: 'var(--spacing-sm)' }}>
                <InfoIcon />
              </div>
            }
          />
        </div>
      )}

      {isBooleanField ? renderBooleanField() : renderSelectField()}
    </div>
  );
};

function isChildOfAnyField(field, fields) {
  if (!field.dependsOn) {
    return false;
  }

  const parentNames = [];
  if (field.dependsOn.allOf || field.dependsOn.anyOf) {
    const conditions = field.dependsOn.allOf || field.dependsOn.anyOf;
    conditions.forEach((cond) => parentNames.push(cond.name));
  } else if (field.dependsOn.name) {
    parentNames.push(field.dependsOn.name);
  }

  // A field is a child if ANY of its dependencies reference another field in the current set
  // (not just the main select field like 'advanced_settings')
  const isChild = parentNames.some((parentName) => fields.some((f) => f.name === parentName));

  return isChild;
}
